import { useEffect, useRef, useState, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { FaTimes, FaDownload } from 'react-icons/fa';
import { MdQrCode2 } from 'react-icons/md';
import { IoGridOutline } from 'react-icons/io5';
import DarkModeContext from './DarkModeContext';
import config from '../Config';
const LOGO_URL = '/icono.ico';

const W = 800;
const H = 1200;
const RED = '#E8254A';

// Imágenes de backend.yourpack.us/storage/ pasan por el proxy de nginx
const toProxied = (src) => {
  if (!src) return src;
  const m = src.match(/^https?:\/\/backend\.yourpack\.us\/storage\/(.+)/);
  return m ? `/storage-proxy/${m[1]}` : src;
};

const loadImg = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const loadImage = async (src) => {
  if (!src) return null;
  const proxied = toProxied(src);
  if (proxied !== src) {
    const img = await loadImg(proxied);
    if (img) return img;
  }
  return loadImg(src);
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const FlyerModal = ({ isOpen, onClose, nickname, profilePhoto, photos = [] }) => {
  const { darkMode } = useContext(DarkModeContext);
  const canvasRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [ready, setReady] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(profilePhoto);
  const [showAllPhotos, setShowAllPhotos] = useState(false);

  const profileUrl = `https://yourpack.us/${nickname}`;

  const allPhotos = [
    ...(profilePhoto ? [profilePhoto] : []),
    ...photos.filter((p) => p && p !== profilePhoto),
  ];

  useEffect(() => {
    setSelectedPhoto(profilePhoto);
  }, [profilePhoto]);

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setGenerating(true);

    const ctx = canvas.getContext('2d');
    canvas.width = W;
    canvas.height = H;

    // ── 1. BACKGROUND PHOTO ──
    const photo = await loadImage(selectedPhoto);
    if (photo) {
      const scale = Math.max(W / photo.width, H / photo.height);
      const sw = photo.width * scale;
      const sh = photo.height * scale;
      ctx.drawImage(photo, (W - sw) / 2, (H - sh) / 2, sw, sh);
    } else {
      const fb = ctx.createLinearGradient(0, 0, 0, H);
      fb.addColorStop(0, '#1a0510');
      fb.addColorStop(1, '#0a0a0f');
      ctx.fillStyle = fb;
      ctx.fillRect(0, 0, W, H);
    }

    // ── 2. GRADIENT OVERLAY (dark only at bottom 22%) ──
    const ovGrad = ctx.createLinearGradient(0, H, 0, 0);
    ovGrad.addColorStop(0,    'rgba(10,10,15,0.70)');
    ovGrad.addColorStop(0.12, 'rgba(10,10,15,0.20)');
    ovGrad.addColorStop(0.22, 'rgba(10,10,15,0.0)');
    ovGrad.addColorStop(1,    'rgba(10,10,15,0.0)');
    ctx.fillStyle = ovGrad;
    ctx.fillRect(0, 0, W, H);

    // ── 3. TOP BAR ──
    const PAD = 28;
    const TOP_Y = 24;

    const logoImg = await loadImage(LOGO_URL);
    if (logoImg) {
      ctx.drawImage(logoImg, PAD, TOP_Y, 130, 130);
    } else {
      ctx.font = 'bold 32px Montserrat, Arial, sans-serif';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText('YourPack', PAD, TOP_Y + 10);
    }

    const QR_BOX = 130;
    const qrBoxX = W - PAD - QR_BOX;
    const qrBoxY = TOP_Y;

    ctx.save();
    roundRect(ctx, qrBoxX, qrBoxY, QR_BOX, QR_BOX, 10);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.restore();

    try {
      const qrDataUrl = await QRCode.toDataURL(profileUrl, {
        width: QR_BOX - 10,
        margin: 1,
        errorCorrectionLevel: 'M',
        color: { dark: '#000000', light: '#ffffff' },
      });
      const qrImg = await loadImage(qrDataUrl);
      if (qrImg) ctx.drawImage(qrImg, qrBoxX + 5, qrBoxY + 5, QR_BOX - 10, QR_BOX - 10);
    } catch {}

    ctx.font = 'bold 18px Montserrat, Arial, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillText(`yourpack.us/${nickname}`, W - PAD, qrBoxY + QR_BOX + 8);

    // ── 4. BOTTOM TEXT ──
    const BOTTOM = H - 36;
    const HL_SIZE = 52;
    const SUB_SIZE = 28;
    const HL_LH = HL_SIZE * 0.93;
    const SUB_LH = SUB_SIZE * 1.1;
    const ARROW_H = 30;
    const ARROW_GAP = 10;

    const masHotY     = BOTTOM;
    const lasModelosY = masHotY - SUB_LH;
    const hablaConY   = lasModelosY - SUB_LH;
    const arrowBotY   = hablaConY - ARROW_GAP;
    const arrowTopY   = arrowBotY - ARROW_H;
    const enVivoY     = arrowTopY - ARROW_GAP;
    const chateaY     = enVivoY - HL_LH;

    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';

    ctx.font = `900 ${HL_SIZE}px Montserrat, "Arial Black", sans-serif`;
    ctx.fillStyle = '#ffffff';
    ctx.fillText('CHATEA', PAD, chateaY);

    ctx.font = `900 ${HL_SIZE}px Montserrat, "Arial Black", sans-serif`;
    ctx.fillStyle = RED;
    ctx.fillText('EN VIVO', PAD, enVivoY);

    const SC = ARROW_H / 44;
    ctx.save();
    ctx.strokeStyle = RED;
    ctx.lineWidth = 3.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(PAD + 8 * SC, arrowTopY + 36 * SC);
    ctx.quadraticCurveTo(PAD + 10 * SC, arrowTopY + 18 * SC, PAD + 36 * SC, arrowTopY + 18 * SC);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(PAD + 30 * SC, arrowTopY + 12 * SC);
    ctx.lineTo(PAD + 36 * SC, arrowTopY + 18 * SC);
    ctx.lineTo(PAD + 28 * SC, arrowTopY + 22 * SC);
    ctx.stroke();
    ctx.restore();

    ctx.font = `800 ${SUB_SIZE}px Montserrat, Arial, sans-serif`;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'left';
    ctx.fillText('HABLA CON', PAD, hablaConY);
    ctx.fillText('LAS MODELOS', PAD, lasModelosY);

    ctx.font = `900 ${SUB_SIZE}px Montserrat, "Arial Black", sans-serif`;
    ctx.fillStyle = RED;
    ctx.fillText('MÁS HOT', PAD, masHotY);

    // ── 5. CHAT MOCK-UP ──
    const CHAT_W = 210;
    const CHAT_H = Math.round(CHAT_W * 15 / 9);
    const chatX = W - PAD - CHAT_W;
    const chatY = BOTTOM - CHAT_H;

    ctx.save();
    roundRect(ctx, chatX, chatY, CHAT_W, CHAT_H, 14);
    ctx.fillStyle = 'rgba(10,10,15,0.65)';
    ctx.fill();
    ctx.restore();

    const HDR_H = 40;
    ctx.save();
    roundRect(ctx, chatX, chatY, CHAT_W, CHAT_H, 14);
    ctx.clip();
    ctx.fillStyle = 'rgba(10,10,15,0.50)';
    ctx.fillRect(chatX, chatY, CHAT_W, HDR_H);
    ctx.restore();

    ctx.font = 'bold 14px Arial';
    ctx.fillStyle = RED;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('←', chatX + 8, chatY + HDR_H / 2);

    const AV_R = 12;
    const avX = chatX + 28;
    const avY = chatY + HDR_H / 2;
    ctx.beginPath();
    ctx.arc(avX, avY, AV_R, 0, Math.PI * 2);
    ctx.fillStyle = RED;
    ctx.fill();
    ctx.font = 'bold 9px Arial';
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(nickname.charAt(0).toUpperCase(), avX, avY);

    ctx.font = 'bold 12px Arial';
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(nickname, chatX + 46, chatY + HDR_H / 2);

    const MSGS = [
      { text: 'Hello?',                          sent: true,  gray: true },
      { text: 'Hello daddy',                     sent: false },
      { text: 'how are you baby?',               sent: true  },
      { text: 'Very good thank and you',         sent: false },
      { text: 'fine baby, you are hot?',         sent: true  },
      { text: 'Yummy hot',                       sent: false },
      { text: 'Want to see me get wet?',         sent: false },
      { text: 'i can hear your voice?',          sent: true  },
      { text: 'sendme a voice note please baby', sent: true  },
    ];

    const MSG_FS = 9.5;
    const MSG_PH = 8;
    const MSG_PV = 4;
    const MSG_MAX = CHAT_W * 0.74;
    const MSG_GAP = 4;
    let msgCurY = chatY + HDR_H + 7;

    ctx.font = `500 ${MSG_FS}px Arial, sans-serif`;

    for (const m of MSGS) {
      if (msgCurY > chatY + CHAT_H - 38) break;
      const tw = Math.min(ctx.measureText(m.text).width, MSG_MAX - MSG_PH * 2);
      const bw = tw + MSG_PH * 2;
      const bh = MSG_FS + MSG_PV * 2 + 3;
      const bx = m.sent ? chatX + CHAT_W - bw - 7 : chatX + 7;

      roundRect(ctx, bx, msgCurY, bw, bh, 7);
      ctx.fillStyle = m.sent ? (m.gray ? '#333333' : RED) : 'rgba(30,30,45,0.85)';
      ctx.fill();

      ctx.font = `500 ${MSG_FS}px Arial, sans-serif`;
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = m.sent ? 'right' : 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        m.text,
        m.sent ? bx + bw - MSG_PH : bx + MSG_PH,
        msgCurY + bh / 2,
        MSG_MAX - MSG_PH * 2,
      );
      msgCurY += bh + MSG_GAP;
    }

    const INP_H = 30;
    const inpY = chatY + CHAT_H - INP_H;
    ctx.save();
    roundRect(ctx, chatX, chatY, CHAT_W, CHAT_H, 14);
    ctx.clip();
    ctx.fillStyle = 'rgba(10,10,15,0.65)';
    ctx.fillRect(chatX, inpY, CHAT_W, INP_H);

    ctx.fillStyle = 'rgba(42,42,58,0.9)';
    ctx.beginPath();
    ctx.arc(chatX + 14, inpY + INP_H / 2, 9, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#888';
    ctx.lineWidth = 1.3;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(chatX + 10, inpY + INP_H / 2);
    ctx.lineTo(chatX + 18, inpY + INP_H / 2);
    ctx.moveTo(chatX + 14, inpY + INP_H / 2 - 4);
    ctx.lineTo(chatX + 14, inpY + INP_H / 2 + 4);
    ctx.stroke();

    roundRect(ctx, chatX + 28, inpY + 5, CHAT_W - 56, INP_H - 10, 10);
    ctx.fillStyle = 'rgba(30,30,45,0.75)';
    ctx.fill();
    ctx.font = `9px Arial`;
    ctx.fillStyle = '#666';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('Write a message...', chatX + 36, inpY + INP_H / 2);

    ctx.fillStyle = 'rgba(42,42,58,0.9)';
    ctx.beginPath();
    ctx.arc(chatX + CHAT_W - 14, inpY + INP_H / 2, 9, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    setGenerating(false);
    setReady(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhoto, nickname, profileUrl]);

  useEffect(() => {
    if (!isOpen || !nickname) { setReady(false); return; }
    setReady(false);
    const t = setTimeout(() => draw(), 80);
    return () => clearTimeout(t);
  }, [isOpen, nickname, draw]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleDownload = () => {
    const params = new URLSearchParams({ nickname, photo_url: selectedPhoto || '' });
    const url = `${config.REACT_APP_BASE_URL}/api/v1/flyer/generate?${params}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `flyer-${nickname}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const pickPhoto = (url) => {
    setSelectedPhoto(url);
    setShowAllPhotos(false);
  };

  if (!isOpen || !nickname) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center px-4"
      style={{ zIndex: 99999 }}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      <div
        className={`relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-rose-500 to-pink-600 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="bg-white/20 rounded-full p-1.5">
              <MdQrCode2 className="text-white text-sm" />
            </div>
            <div>
              <p className="text-white font-semibold text-base leading-tight">Generar Flyer</p>
              <p className="text-white/75 text-xs" translate="no">@{nickname}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors"
            aria-label="Cerrar"
          >
            <FaTimes className="text-sm" />
          </button>
        </div>

        {/* Photo picker carousel */}
        {allPhotos.length > 0 && (
          <div className={`px-4 pt-4 pb-0 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Foto de fondo
            </p>
            <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
              {allPhotos.slice(0, 4).map((url, i) => (
                <button
                  key={i}
                  onClick={() => pickPhoto(url)}
                  className="flex-shrink-0 relative rounded-xl overflow-hidden"
                  style={{ width: 52, height: 52 }}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  {selectedPhoto === url && (
                    <div className="absolute inset-0 ring-2 ring-rose-500 ring-offset-1 rounded-xl" />
                  )}
                </button>
              ))}

              {/* "Ver todas" button */}
              <button
                onClick={() => setShowAllPhotos(true)}
                className={`flex-shrink-0 rounded-xl flex flex-col items-center justify-center gap-1 transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                style={{ width: 52, height: 52 }}
              >
                <IoGridOutline className="text-lg" />
                <span className="text-[9px] font-semibold leading-none">Ver todas</span>
              </button>
            </div>
          </div>
        )}

        {/* Canvas preview */}
        <div className="px-5 py-4 flex flex-col gap-4">
          <div
            className={`relative rounded-xl overflow-hidden border ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-100'}`}
            style={{ aspectRatio: `${W}/${H}` }}
          >
            {generating && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-rose-500 border-t-transparent" />
              </div>
            )}
            <canvas
              ref={canvasRef}
              className="w-full h-full"
              style={{ display: ready ? 'block' : 'none' }}
            />
          </div>

          <button
            onClick={handleDownload}
            disabled={!ready}
            className="flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors text-sm"
          >
            <FaDownload className="text-xs" />
            Descargar Flyer
          </button>
        </div>
      </div>

      {/* All-photos overlay */}
      {showAllPhotos && (
        <div
          className="absolute inset-0 flex items-center justify-center px-4"
          style={{ zIndex: 100000 }}
          onClick={() => setShowAllPhotos(false)}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            className={`relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden flex flex-col ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
            style={{ maxHeight: '80vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Todas las fotos ({allPhotos.length})
              </p>
              <button
                onClick={() => setShowAllPhotos(false)}
                className={`rounded-full w-7 h-7 flex items-center justify-center ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <FaTimes className="text-sm" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 min-h-0">
              <div className="p-3 grid grid-cols-3 gap-2">
                {allPhotos.map((url, i) => (
                  <button
                    key={i}
                    onClick={() => pickPhoto(url)}
                    className="relative rounded-xl overflow-hidden"
                    style={{ aspectRatio: '1 / 1' }}
                  >
                    <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    {selectedPhoto === url && (
                      <div className="absolute inset-0 ring-2 ring-rose-500 ring-inset rounded-xl bg-rose-500/20" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default FlyerModal;
